import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Board from './components/Board';
import Footer from './components/Footer';
import LoginModal from './components/LoginModal';
import RequestModal from './components/RequestModal';
import CreateTaskModal from './components/CreateTaskModal';
import TaskDetailModal from './components/TaskDetailModal';
import RequestsPanel from './components/RequestsPanel';
import ManageUsersModal from './components/ManageUsersModal';
import ChangePasswordModal from './components/ChangePasswordModal';
import ProfileEditorModal from './components/ProfileEditorModal';
import LogsModal from './components/LogsModal';
import SettingDataModal from './components/SettingDataModal';
import api from './utils/api';

const STATUS_ORDER = ['new', 'in_progress', 'on_hold', 'in_review', 'done', 'cancelled'];

function normalizeTaskOrder(taskList) {
  const grouped = Object.fromEntries(STATUS_ORDER.map((status) => [status, []]));
  const extras = [];

  taskList.forEach((task) => {
    const nextTask = { ...task };
    if (grouped[nextTask.status]) {
      grouped[nextTask.status].push(nextTask);
    } else {
      extras.push(nextTask);
    }
  });

  STATUS_ORDER.forEach((status) => {
    grouped[status].sort((a, b) => {
      const diff = Number(a.sort_order || 0) - Number(b.sort_order || 0);
      if (diff !== 0) return diff;
      return Number(a.id) - Number(b.id);
    });
    grouped[status].forEach((task, index) => {
      task.status = status;
      task.sort_order = index;
    });
  });

  return [...STATUS_ORDER.flatMap((status) => grouped[status]), ...extras];
}

function moveTaskInState(taskList, taskId, nextStatus, nextIndex = null) {
  const grouped = Object.fromEntries(STATUS_ORDER.map((status) => [status, []]));
  const cloned = taskList.map((task) => ({ ...task }));

  cloned.forEach((task) => {
    if (grouped[task.status]) {
      grouped[task.status].push(task);
    }
  });

  STATUS_ORDER.forEach((status) => {
    grouped[status].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
  });

  let movingTask = null;
  let sourceStatus = null;
  let sourceIndex = -1;

  for (const status of STATUS_ORDER) {
    const index = grouped[status].findIndex((task) => Number(task.id) === Number(taskId));
    if (index >= 0) {
      sourceStatus = status;
      sourceIndex = index;
      [movingTask] = grouped[status].splice(index, 1);
      break;
    }
  }

  if (!movingTask || !grouped[nextStatus]) {
    return normalizeTaskOrder(cloned);
  }

  movingTask.status = nextStatus;

  const destination = grouped[nextStatus];
  let insertIndex = nextIndex == null ? destination.length : Math.max(0, Math.min(nextIndex, destination.length));
  if (sourceStatus === nextStatus && sourceIndex >= 0 && insertIndex > sourceIndex) {
    insertIndex -= 1;
  }
  destination.splice(insertIndex, 0, movingTask);

  return normalizeTaskOrder(STATUS_ORDER.flatMap((status) => grouped[status]));
}

function buildTasksSignature(taskList) {
  return (taskList || [])
    .map((task) => [
      task.id,
      task.status,
      task.sort_order,
      task.updated_at,
      task.title,
      task.visibility,
      task.deadline,
      task.task_company,
      task.can_open ? 1 : 0,
      task.is_masked ? 1 : 0,
    ].join('|'))
    .join('||');
}

function AppContent() {
  const { user, loading, canCreateTask } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState({ task_company_options: [] });
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 900);
  const [showLogin, setShowLogin] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [showManageUsers, setShowManageUsers] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [showSettingData, setShowSettingData] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const tasksSignatureRef = useRef('');
  const profileSignatureRef = useRef('');
  const settingsSignatureRef = useRef('');

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchTasks = useCallback(async ({ force = false } = {}) => {
    try {
      const data = await api.getTasks();
      const nextTasks = normalizeTaskOrder(data.tasks || []);
      const nextSignature = buildTasksSignature(nextTasks);

      if (force || tasksSignatureRef.current !== nextSignature) {
        tasksSignatureRef.current = nextSignature;
        setTasks(nextTasks);
      }

      if (Array.isArray(data.company_options) && data.company_options.length) {
        setSettings((prev) => {
          const currentOptions = Array.isArray(prev.task_company_options) ? prev.task_company_options : [];
          const sameOptions = currentOptions.length === data.company_options.length && currentOptions.every((item, index) => item === data.company_options[index]);
          return sameOptions ? prev : { ...prev, task_company_options: data.company_options };
        });
      }
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoadingTasks(false);
    }
  }, []);

  const fetchProfile = useCallback(async ({ force = false } = {}) => {
    try {
      const data = await api.getProfile();
      const nextProfile = data.profile || null;
      const nextSignature = JSON.stringify(nextProfile || {});
      if (force || profileSignatureRef.current !== nextSignature) {
        profileSignatureRef.current = nextSignature;
        setProfile(nextProfile);
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    }
  }, []);

  const fetchSettings = useCallback(async ({ force = false } = {}) => {
    if (!user) {
      return;
    }

    try {
      const data = await api.getSettings();
      if (data.settings) {
        const nextSignature = JSON.stringify(data.settings);
        if (force || settingsSignatureRef.current !== nextSignature) {
          settingsSignatureRef.current = nextSignature;
          setSettings(data.settings);
        }
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }, [user]);

  const fetchUserSuggestions = useCallback(async () => {
    if (!user) {
      setUserSuggestions([]);
      return;
    }

    try {
      const data = await api.getUsers();
      const suggestions = (data.users || [])
        .map((item) => item.display_name)
        .filter(Boolean)
        .filter((value, index, array) => array.indexOf(value) === index)
        .sort((a, b) => a.localeCompare(b, 'vi'));
      setUserSuggestions(suggestions);
    } catch (err) {
      console.error('Failed to fetch user suggestions:', err);
    }
  }, [user]);

  useEffect(() => {
    fetchTasks({ force: true });
    fetchProfile({ force: true });
  }, [fetchTasks, fetchProfile, user?.id]);

  useEffect(() => {
    fetchSettings({ force: true });
    fetchUserSuggestions();
  }, [fetchSettings, fetchUserSuggestions]);

  useEffect(() => {
    const poll = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      fetchTasks();
      fetchProfile();
      if (user) {
        fetchSettings();
      }
    };

    const interval = window.setInterval(poll, 5000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        poll();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', poll);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', poll);
    };
  }, [fetchTasks, fetchProfile, fetchSettings, user]);

  const handleTaskClick = useCallback((task) => {
    if (!task.can_open) return;
    setSelectedTask(task);
  }, []);

  const handleTaskCreated = useCallback(() => {
    fetchTasks({ force: true });
    setShowCreateTask(false);
  }, [fetchTasks]);

  const handleTaskUpdated = useCallback(() => {
    fetchTasks({ force: true });
  }, [fetchTasks]);

  const handleTaskMoved = useCallback(async (taskId, newStatus, newIndex = null) => {
    const currentTasks = tasks;
    const nextTasks = moveTaskInState(currentTasks, taskId, newStatus, newIndex);
    tasksSignatureRef.current = buildTasksSignature(nextTasks);
    setTasks(nextTasks);

    const movedTask = nextTasks.find((task) => Number(task.id) === Number(taskId));
    try {
      await api.moveTask(taskId, newStatus, movedTask?.sort_order ?? newIndex ?? 0);
    } catch (err) {
      console.error('Failed to move task:', err);
      tasksSignatureRef.current = buildTasksSignature(currentTasks);
      setTasks(currentTasks);
      fetchTasks({ force: true });
    }
  }, [tasks, fetchTasks]);

  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);

  if (loading) {
    return (
      <div className="fullscreen-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <>
      <Header
        sidebarOpen={sidebarOpen}
        onToggleSidebar={toggleSidebar}
        onLogin={() => setShowLogin(true)}
        onOpenRequest={() => setShowRequest(true)}
        onCreateTask={() => setShowCreateTask(true)}
        onShowRequests={() => setShowRequests(true)}
        onManageUsers={() => setShowManageUsers(true)}
        onShowLogs={() => setShowLogs(true)}
        onChangePassword={() => setShowChangePassword(true)}
        onEditProfile={() => setShowProfileEditor(true)}
        onShowSettingData={() => setShowSettingData(true)}
        isMobile={isMobile}
      />

      <div className="app-layout">
        {isMobile && sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
        <Sidebar open={sidebarOpen} profile={profile} tasks={tasks} />
        <div className="main-content">
          {loadingTasks ? (
            <div className="fullscreen-center"><div className="spinner" /></div>
          ) : (
            <Board
              tasks={tasks}
              onTaskClick={handleTaskClick}
              onTaskMoved={handleTaskMoved}
              canDrag={canCreateTask}
              isGuest={!user}
            />
          )}
          {!isMobile && <Footer />}
        </div>
      </div>

      <a href="https://zalo.me/0777765425" target="_blank" rel="noopener noreferrer" className="zalo-float-btn" aria-label="Liên hệ Zalo">
        Zalo
      </a>

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      {showRequest && <RequestModal onClose={() => setShowRequest(false)} />}
      {showCreateTask && (
        <CreateTaskModal
          onClose={() => setShowCreateTask(false)}
          onCreated={handleTaskCreated}
          companyOptions={settings.task_company_options}
          assigneeSuggestions={userSuggestions}
        />
      )}
      {selectedTask && (
        <TaskDetailModal
          taskId={selectedTask.id}
          onClose={() => setSelectedTask(null)}
          onUpdated={handleTaskUpdated}
          companyOptions={settings.task_company_options}
          assigneeSuggestions={userSuggestions}
        />
      )}
      {showRequests && <RequestsPanel onClose={() => setShowRequests(false)} onRefresh={fetchTasks} />}
      {showManageUsers && <ManageUsersModal onClose={() => setShowManageUsers(false)} />}
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
      {showProfileEditor && <ProfileEditorModal onClose={() => setShowProfileEditor(false)} onUpdated={fetchProfile} />}
      {showLogs && <LogsModal onClose={() => setShowLogs(false)} />}
      {showSettingData && (
        <SettingDataModal
          onClose={() => setShowSettingData(false)}
          settings={settings}
          onUpdated={(nextSettings) => setSettings((prev) => ({ ...prev, ...nextSettings }))}
        />
      )}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
